import { useEffect, useState } from 'react';
import { DataGrid } from '../../components/grid/DataGrid';
import { SchedaCommessa } from './SchedaCommessa';
import { idDallAncora } from '../../lib/ancora';
import { useSessione } from '../sessione/ContestoSessione';

const INTESTAZIONI: Record<string, string> = {
  OrdineCliente: 'Ordine cliente',
  InizioLavori: 'Inizio',
  FinePrevista: 'Fine prevista',
  GiorniAllaFine: 'Giorni',
  OltreLaFinePrevista: 'In ritardo',
  PrezzoConcordato: 'Concordato',
  BudgetMateriali: 'Budget materiali',
  BudgetOre: 'Budget manodopera',
  BudgetCosti: 'Budget costi',
  MaterialiDaOrdinare: 'Da ordinare',
  MaterialiImpegnati: 'Impegnati',
  MaterialiRicevuti: 'Ricevuti',
  MaterialiPrevisti: 'Materiali previsti',
  MaterialiSulBudget: '% budget materiali',
  OreLavorate: 'Ore fatte',
  OreSulBudget: '% budget manodopera',
  CostoOreStimato: 'Costo manodopera',
  CostiPrevisti: 'Costi previsti',
  MarginePrevisto: 'Margine',
  MarginePercentuale: 'Margine %',
};

/*  Le colonne aperte sono quelle che servono per rispondere a «quale commessa sta andando
    male»: stato, scadenza, i materiali nelle tre fasi, le ore, il margine. Il resto
    (budget di dettaglio, conteggi dei documenti, ordine del cliente) resta fuori: una
    griglia con ventotto colonne non si legge, e la prima cosa che farebbe chi la apre e'
    spegnerne venti.

    Fuori vuol dire fuori davvero, e vale la pena saperlo: non c'e' un menu che le
    riaccenda — quello sull'intestazione filtra e ordina, non sceglie le colonne. Una
    colonna dichiarata dal registro e non elencata qui non e' nascosta, e' irraggiungibile.
    Resta comunque cercabile dal riquadro «Cerca», che interroga anche quelle spente.

    Il numero dell'offerta e' qui e non fra le spente perche' e' un dato d'identita' come
    il numero della commessa: scorrendo l'elenco, «da quale offerta e' nata» e' la domanda
    che si fa prima di aprire, non dopo.  */
const COLONNE = [
  'Numero',
  'Sede',
  'Stato',
  'Cliente',
  'Preventivo',
  'Descrizione',
  'Responsabile',
  'FinePrevista',
  'OltreLaFinePrevista',
  'PrezzoConcordato',
  'MaterialiDaOrdinare',
  'MaterialiImpegnati',
  'MaterialiRicevuti',
  'MaterialiSulBudget',
  'OreLavorate',
  'OreSulBudget',
  'CostiPrevisti',
  'MarginePrevisto',
  'MarginePercentuale',
];

export function ElencoCommesse() {
  const sessione = useSessione();
  /*  Si parte da «tutte»: le commesse aperte sono poche, e chi entra la prima volta deve
      vederle. «Le mie» e' un clic.  */
  const [vista, setVista] = useState<'mie' | 'tutte'>('tutte');
  const [aperta, setAperta] = useState<string | null>(() => idDallAncora('commesse'));

  /*  Un'ancora con dentro un identificativo apre quel documento: e' cosi' che
      arriva chi ha premuto il collegamento su un'altra scheda, o chi ha incollato
      un indirizzo ricevuto in una mail. Si legge anche dopo, perche' il
      collegamento puo' arrivare quando la schermata e' gia' aperta e il tasto
      «indietro» del browser deve riportare dov'era.  */
  useEffect(() => {
    const rileggi = () => {
      const id = idDallAncora('commesse');
      if (id !== null) setAperta(id);
    };

    window.addEventListener('hashchange', rileggi);
    return () => window.removeEventListener('hashchange', rileggi);
  }, []);

  return (
    <section className="pagina con-scheda">
      <div className="colonna-principale">
        <header className="testata-pagina">
          <div>
            <h1>Commesse</h1>
            <p>
              Budget contro speso, una riga per commessa. Il materiale è diviso in tre fasi
              perché sono tre cose diverse: <strong>da ordinare</strong> è quello che il
              cantiere ha chiesto e nessuno ha ancora comprato, <strong>impegnato</strong> è
              quello che sta su un ordine emesso — soldi promessi al fornitore anche se la
              merce non è arrivata — e <strong>ricevuto</strong> è quello che è entrato in
              magazzino. «Ricevuto» è dentro «impegnato»: non vanno sommate. Apri una riga per
              vedere da dove viene ogni numero — quali ordini, quali righe ancora da comprare,
              chi ha fatto le ore.
            </p>
            <p className="nota">
              Una cosa che questi numeri non sanno. Il costo delle ore usa il costo orario di
              oggi dell’operaio, non quello che aveva quando ha lavorato: su una commessa
              lunga un aumento di paga riscrive il passato, ed è per questo che il costo
              della manodopera è una stima e non un consuntivo. Le trasferte invece sono
              ferme: chilometri e indennità si congelano sul rapportino il giorno in cui
              viene approvato, e non cambiano più.
            </p>
          </div>
        </header>

        {/*  «LE MIE COMMESSE», 16/10/2026: «io guardo dentro commesse personali e mi vengono
             proprio le commesse dove c'e' il mio nome». E' un filtro sul responsabile, come
             gli altri: si vede e si toglie. Tutte e due le viste lasciano fuori le chiuse.  */}
        {sessione.nomeCompleto !== null && (
          <div className="viste-pronte" role="group" aria-label="Viste">
            {(['mie', 'tutte'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={vista === v ? 'on' : ''}
                aria-pressed={vista === v}
                onClick={() => setVista(v)}
              >
                {v === 'mie' ? 'Le mie' : 'Tutte'}
              </button>
            ))}
          </div>
        )}

        <DataGrid
          key={vista}
          filtriIniziali={
            vista === 'mie'
              ? [
                  { colonna: 'Responsabile', operatore: 'EUnoDi', valori: [sessione.nomeCompleto] },
                  { colonna: 'Stato', operatore: 'NonEUnoDi', valori: ['Chiusa'] },
                ]
              : undefined
          }
          nomeGriglia="commesse"
          colonneVisibili={COLONNE}
          intestazioni={INTESTAZIONI}
          idSelezionato={aperta}
          onRigaSelezionata={(riga) => {
            const id = riga['Id'];
            if (typeof id === 'string') setAperta(id);
          }}
        />
      </div>

      {aperta && <SchedaCommessa commessaId={aperta} onChiudi={() => setAperta(null)} />}
    </section>
  );
}
